import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Avatar,
  Paper,
  IconButton,
  Stack,
  Chip,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Settings,
  Twitter,
  Instagram,
  GitHub,
  Link as LinkIcon,
  Favorite,
  ExitToApp,
  LocationOn,
  CalendarToday,
  Group,
  Image,
  Chat,
  Palette,
  MenuBook,
  Star,
  Groups,
  EmojiEvents,
  QrCode
} from '@mui/icons-material';

const UserProfile = ({ userInfo }) => {
  const [tabValue, setTabValue] = useState('gallery');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ 
      width: '100%', 
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #F8FAFC, #F1F5F9)'
    }}>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative', 
        height: 500,
        overflow: 'hidden'
      }}>
        {/* Background Image and Overlay */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.5), rgba(15,23,42,0.8))'
          }
        }}>
          <Box
            component="img"
            src="/api/placeholder/1920/1080"
            alt="カバー画像"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </Box>

        {/* Profile Content */}
        <Box sx={{
          position: 'relative',
          height: '100%',
          maxWidth: 1200,
          mx: 'auto',
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          pb: 10
        }}>
          <Stack direction="row" spacing={4} alignItems="flex-end">
            {/* Profile Image */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src="/api/placeholder/400/400"
                alt={userInfo?.name}
                sx={{
                  width: 128,
                  height: 128,
                  border: '4px solid rgba(255,255,255,0.2)',
                  borderRadius: 4,
                  boxShadow: 4,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 24,
                  height: 24,
                  bgcolor: 'success.main',
                  borderRadius: '50%',
                  border: '4px solid white'
                }}
              />
            </Box>

            {/* Profile Info */}
            <Box sx={{ flex: 1, color: 'white' }}>
              <Typography variant="h3" fontWeight="bold" mb={2}>
                {userInfo?.name || 'クリエイター'}
              </Typography>
              <Typography variant="body1" color="grey.300" mb={3} maxWidth="xl">
                {userInfo?.bio || 'アートとテクノロジーを組み合わせて、ユニークな作品を制作しています。'}
              </Typography>

              <Stack spacing={2}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOn fontSize="small" />
                    <Typography>東京</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarToday fontSize="small" />
                    <Typography>2024年から活動中</Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={3} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Group fontSize="small" />
                    <Typography>4.2万フォロワー</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmojiEvents fontSize="small" />
                    <Typography>156サブスクリプション</Typography>
                  </Stack>
                  <Chip
                    label="本人確認未提出"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white'
                    }}
                  />
                </Stack>
              </Stack>

              <Button
                startIcon={<QrCode />}
                sx={{
                  mt: 2,
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    color: 'white'
                  }
                }}
                onClick={() => window.alert('QRコードを表示')}
              >
                プロフィールのQRコードを表示
              </Button>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                sx={{
                  px: 3,
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                フォロー
              </Button>
              <IconButton
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                <Chat />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: 4,
        mt: -6
      }}>
        <Paper
          elevation={3}
          sx={{
            bgcolor: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(16px)',
            borderRadius: 6,
            p: 4
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ mb: 4 }}
            TabIndicatorProps={{
              sx: { display: 'none' }
            }}
          >
            {[
              { value: 'gallery', icon: <Image />, label: 'ギャラリー' },
              { value: 'recommend', icon: <Star />, label: 'おすすめ' },
              { value: 'character', icon: <Palette />, label: 'キャラクター' },
              { value: 'series', icon: <MenuBook />, label: 'シリーズ' },
              { value: 'community', icon: <Groups />, label: '交流' },
              { value: 'plan', icon: <EmojiEvents />, label: 'プラン' },
              { value: 'stats', icon: <Group />, label: '統計' }
            ].map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                label={tab.label}
                sx={{
                  minHeight: 'auto',
                  px: 3,
                  py: 1,
                  mr: 1,
                  borderRadius: 2,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'white',
                    bgcolor: 'primary.main'
                  }
                }}
              />
            ))}
          </Tabs>

          {/* Gallery Tab Content */}
          {tabValue === 'gallery' && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <FormControl size="small">
                  <Select defaultValue="post_desc">
                    <MenuItem value="post_desc">投稿日新しい順</MenuItem>
                    <MenuItem value="post_asc">投稿日古い順</MenuItem>
                    <MenuItem value="favorite_desc">いいね多い順</MenuItem>
                    <MenuItem value="pv_desc">閲覧数多い順</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label="単品販売"
                />
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label="メンバーシップ"
                />
              </Stack>

              <Grid container spacing={2}>
                {[...Array(8)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '100%',
                        borderRadius: 4,
                        overflow: 'hidden',
                        '&:hover': {
                          '& img': {
                            transform: 'scale(1.1)'
                          },
                          '& .overlay': {
                            opacity: 1
                          }
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={`/api/placeholder/${300 + i}/${300 + i}`}
                        alt={`作品 ${i + 1}`}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s'
                        }}
                      />
                      <Box
                        className="overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                          opacity: 0,
                          transition: 'opacity 0.3s',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          p: 2
                        }}
                      >
                        <Typography color="white" fontWeight="medium">
                          作品 {i + 1}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" color="rgba(255,255,255,0.8)">
                          <Favorite fontSize="small" />
                          <Typography variant="body2">1.2k</Typography>
                        </Stack>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Add other tab contents similarly */}
        </Paper>
      </Box>
    </Box>
  );
};

export default UserProfile;